import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { CertificatesService } from 'services/professional/certificates/certificates.service';
import { CertificateRejectModalComponent } from 'components/modal-windows/certificate/reject-modal/certificate-reject-modal.component';
import { constants } from 'constants/constants';
import { MessageQueryService, skipModalClose, SmartModalService } from '@certemy/ui-components';
import {
  AcceptActionType,
  AcceptCertificationComponent,
} from 'components/certificate-assign-request/accept/accept.component';
import { NEVER } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import {
  CertificateStatusChangeAction,
  CertificateStatusChangeEvent,
} from 'services/professional/certificates/certificates.interface';
import { Store } from '@ngrx/store';
import { Selectors } from 'features/profiles-state/+state/profiles-state.state';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { WORKFLOW_MODULE_ID } from 'constants/workflow.constants';

export interface CertificateAssignRequestData {
  certificate_id: number;
  certificate_name: string;
  certificate_template_id: number;
  status_color: number;
  certification_logo?: string;
  typeId?: number;
}

@UntilDestroy()
@Component({
  selector: 'cm-certificate-assign-request',
  templateUrl: './certificate-assign-request.component.html',
  styleUrls: ['./certificate-assign-request.component.scss'],
})
export class CertificateAssignRequestComponent implements OnInit, OnDestroy {
  @Input() certificate: CertificateAssignRequestData;
  @Output() certificateStatusChange = new EventEmitter<CertificateStatusChangeEvent>();
  isLoggedAsProfessional = false;

  constructor(
    private router: Router,
    private store: Store<any>,
    private certificatesService: CertificatesService,
    private smartModalService: SmartModalService,
    private messageQuery: MessageQueryService,
  ) {}

  ngOnInit() {
    this.store
      .select(Selectors.currentProfile)
      .pipe(take(1))
      .subscribe((profile) => (this.isLoggedAsProfessional = !!profile.origin));
  }

  ngOnDestroy() {}

  rejectCertificate(event) {
    event.stopPropagation();
    this.smartModalService
      .open(
        CertificateRejectModalComponent,
        {
          isLoggedAsProfessional: this.isLoggedAsProfessional,
          certificate: this.certificate,
        },
        { size: 'lg' },
      )
      .pipe(
        untilDestroyed(this),
        skipModalClose(),
        catchError(() => NEVER),
        switchMap(() =>
          this.certificatesService.updateCertificateAssignStatus(this.certificate.certificate_id, {
            status_id: constants.CERTIFICATE_STATUSES.REJECT,
          }),
        ),
      )
      .subscribe(
        () => {
          this.onCertificateActionSuccess(constants.CERTIFICATE_STATUSES.REJECT);
          this.router.navigate(['/professional/certifications']);
        },
        () => this.onCertificationActionError(constants.CERTIFICATE_STATUSES.REJECT),
      );
  }

  acceptCertificate(event) {
    event.stopPropagation();

    if (
      this.certificate.typeId === WORKFLOW_MODULE_ID.COMPLIANCE ||
      this.certificate.certificate_template_id === constants.CERTIFICATION_TEMPLATE.TEST_REGISTRATION
    ) {
      this.sendAccept();
      return;
    }

    this.showAcceptModal();
  }

  private onCertificateActionSuccess(status) {
    const isAccept = status === constants.CERTIFICATE_STATUSES.ACCEPT;
    const statusName = isAccept ? 'accepted' : 'rejected';
    const action = isAccept ? CertificateStatusChangeAction.ACCEPT : CertificateStatusChangeAction.REJECT;
    this.certificateStatusChange.emit({
      certificate_id: this.certificate.certificate_id,
      action,
    });
    this.messageQuery.addMessage(`The credential was ${statusName}`);
  }

  private onCertificationActionError(status) {
    const process = status === constants.CERTIFICATE_STATUSES.ACCEPT ? 'accepting' : 'rejecting';
    this.messageQuery.addErrorMessage(`An error occurred while ${process} the credential. Please, try again`);
  }

  private showAcceptModal() {
    this.smartModalService
      .open(AcceptCertificationComponent, { cert: this.certificate }, { size: 'lg' })
      .pipe(
        skipModalClose(),
        untilDestroyed(this),
        catchError(() => NEVER),
      )
      .subscribe(({ payload }) => {
        if (payload.type === AcceptActionType.Accept) {
          this.sendAccept();
        } else if (payload.type === AcceptActionType.UploadForVerification) {
          this.uploadCertificateForVerifier(payload.payload);
        }
      });
  }

  private sendAccept() {
    this.certificatesService
      .updateCertificateAssignStatus(this.certificate.certificate_id, {
        status_id: constants.CERTIFICATE_STATUSES.ACCEPT,
      })
      .subscribe(
        () => this.onCertificateActionSuccess(constants.CERTIFICATE_STATUSES.ACCEPT),
        () => this.onCertificationActionError(constants.CERTIFICATE_STATUSES.ACCEPT),
      );
  }

  private uploadCertificateForVerifier(certificateForVerification) {
    this.certificatesService
      .uploadCertificateForVerifier(this.certificate.certificate_id, certificateForVerification)
      .subscribe(
        () => this.onCertificateActionSuccess(constants.CERTIFICATE_STATUSES.ACCEPT),
        () => this.onCertificationActionError(constants.CERTIFICATE_STATUSES.ACCEPT),
      );
  }
}
